/**
 * Prime Model Tester Component
 * 
 * Provides testing interface for prime models including:
 * - Flow validation and connectivity analysis
 * - State machine testing
 * - Test results visualization
 * - Recommendations for improvement
 */

import React, { useState } from 'react';
import {
  Card,
  Button,
  Alert,
  Progress,
  List,
  Badge,
  Spin,
  Tabs,
  Collapse,
  Statistic,
  Row,
  Col,
  Typography,
  Space,
  Divider
} from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import styles from './PrimeModelTester.module.css';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;

interface TestResults {
  test_id: string;
  test_name: string;
  prime_artifact_id: string;
  prime_artifact_name: string;
  test_timestamp: string;
  completeness_analysis: {
    completeness_score: number;
    warnings: string[];
    unused_states: string[];
    incomplete_processes: any[];
    inaccessible_states: string[];
    missing_transitions: any[];
    validation_issues: any[];
  };
  flow_analysis: {
    connectivity_score: number;
    unreachable_states: string[];
    dead_end_states: string[];
    cycles: string[][];
    state_count: number;
    transition_count: number;
  };
  item_lifecycle_test: {
    success: boolean;
    items_created: number;
    transitions_tested: number;
    successful_transitions: number;
    available_transitions: number;
    error?: string;
  };
  overall_success: boolean;
  recommendations: string[];
}

interface PrimeModelTesterProps {
  artifactId: string;
  artifactName: string;
  onTestComplete?: (_results: TestResults) => void;
}

export const PrimeModelTester: React.FC<PrimeModelTesterProps> = ({
  artifactId,
  artifactName,
  onTestComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [propertyAnalysis, setPropertyAnalysis] = useState<any>(null);
  const [propertyValidation, setPropertyValidation] = useState<any>(null);

  const fetchPropertyData = async () => {
    try {
      const [analysisResponse, validationResponse] = await Promise.all([
        apiClient.get(`/prime-artifacts/${artifactId}/property-analysis`),
        apiClient.get(`/prime-artifacts/${artifactId}/validate-properties`)
      ]);
      
      setPropertyAnalysis(analysisResponse.data);
      setPropertyValidation(validationResponse.data);
    } catch (error) {
      console.error('Error fetching property data:', error);
    }
  };

  const runTest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/prime-artifacts/${artifactId}/test-flow`, {
        test_name: `Test_${artifactName}_${new Date().toISOString().slice(0, 19)}`
      });
      
      if (response.data.success) {
        const results = response.data.test_results;
        setTestResults(results);
        onTestComplete?.(results);
        
        // Also fetch property data
        await fetchPropertyData();
      } else {
        setError('Test execution failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Test execution failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (success: boolean) => {
    return success ? (
      <CheckCircleOutlined style={{ color: '#52c41a' }} />
    ) : (
      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
    );
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return '#52c41a';
    if (score >= 0.6) return '#faad14';
    return '#ff4d4f';
  };

  const renderOverview = () => {
    if (!testResults) {
      return (
        <div className={styles.testPlaceholder}>
          <InfoCircleOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={4}>Ready to Test</Title>
          <Paragraph>
            Click "Run Test" to validate the prime model's connectivity and flow.
            This will create a temporary company instance and exercise the state machine.
          </Paragraph>
        </div>
      );
    }

    const { completeness_analysis, flow_analysis, item_lifecycle_test, overall_success } = testResults;

    return (
      <div className={styles.testOverview}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Overall Success"
                value={overall_success ? 'PASS' : 'FAIL'}
                valueStyle={{ color: overall_success ? '#52c41a' : '#ff4d4f' }}
                prefix={getStatusIcon(overall_success)}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Completeness Score"
                value={Math.round(completeness_analysis.completeness_score * 100)}
                suffix="%"
                valueStyle={{ color: getScoreColor(completeness_analysis.completeness_score) }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Connectivity Score"
                value={Math.round(flow_analysis.connectivity_score * 100)}
                suffix="%"
                valueStyle={{ color: getScoreColor(flow_analysis.connectivity_score) }}
              />
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Flow Analysis" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>States: </Text>
                  <Badge count={flow_analysis.state_count} style={{ backgroundColor: '#1890ff' }} />
                </div>
                <div>
                  <Text strong>Transitions: </Text>
                  <Badge count={flow_analysis.transition_count} style={{ backgroundColor: '#52c41a' }} />
                </div>
                {flow_analysis.unreachable_states.length > 0 && (
                  <Alert
                    message={`${flow_analysis.unreachable_states.length} unreachable states`}
                    type="warning"
                  />
                )}
                {flow_analysis.dead_end_states.length > 0 && (
                  <Alert
                    message={`${flow_analysis.dead_end_states.length} dead-end states`}
                    type="warning"
                  />
                )}
              </Space>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Item Lifecycle Test" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Items Created: </Text>
                  <Badge count={item_lifecycle_test.items_created} style={{ backgroundColor: '#52c41a' }} />
                </div>
                <div>
                  <Text strong>Transitions Tested: </Text>
                  <Badge count={item_lifecycle_test.transitions_tested} style={{ backgroundColor: '#1890ff' }} />
                </div>
                <div>
                  <Text strong>Successful: </Text>
                  <Badge count={item_lifecycle_test.successful_transitions} style={{ backgroundColor: '#52c41a' }} />
                </div>
                {!item_lifecycle_test.success && (
                  <Alert
                    message="Item lifecycle test failed"
                    description={item_lifecycle_test.error}
                    type="error"
                  />
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderDetailedResults = () => {
    if (!testResults) return null;

    const { completeness_analysis, flow_analysis, item_lifecycle_test } = testResults;

    return (
      <div className={styles.detailedResults}>
        <Collapse defaultActiveKey={['completeness', 'flow', 'lifecycle']}>
          <Panel header="Completeness Analysis" key="completeness">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Progress
                percent={Math.round(completeness_analysis.completeness_score * 100)}
                strokeColor={getScoreColor(completeness_analysis.completeness_score)}
              />
              
              {completeness_analysis.warnings.length > 0 && (
                <Alert
                  message="Warnings"
                  description={
                    <List
                      size="small"
                      dataSource={completeness_analysis.warnings}
                      renderItem={(item) => <List.Item>{item}</List.Item>}
                    />
                  }
                  type="warning"
                />
              )}

              {completeness_analysis.unused_states.length > 0 && (
                <Alert
                  message="Unused States"
                  description={completeness_analysis.unused_states.join(', ')}
                  type="info"
                />
              )}

              {completeness_analysis.incomplete_processes.length > 0 && (
                <Alert
                  message="Incomplete Processes"
                  description={
                    <List
                      size="small"
                      dataSource={completeness_analysis.incomplete_processes}
                      renderItem={(item) => (
                        <List.Item>
                          <Text strong>{item.process}:</Text> {item.issue}
                        </List.Item>
                      )}
                    />
                  }
                  type="warning"
                />
              )}
            </Space>
          </Panel>

          <Panel header="Flow Analysis" key="flow">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Progress
                percent={Math.round(flow_analysis.connectivity_score * 100)}
                strokeColor={getScoreColor(flow_analysis.connectivity_score)}
              />

              {flow_analysis.unreachable_states.length > 0 && (
                <Alert
                  message="Unreachable States"
                  description={flow_analysis.unreachable_states.join(', ')}
                  type="error"
                />
              )}

              {flow_analysis.dead_end_states.length > 0 && (
                <Alert
                  message="Dead-End States"
                  description={flow_analysis.dead_end_states.join(', ')}
                  type="warning"
                />
              )}

              {flow_analysis.cycles.length > 0 && (
                <Alert
                  message="State Cycles Detected"
                  description={`${flow_analysis.cycles.length} cycles found`}
                  type="info"
                />
              )}
            </Space>
          </Panel>

          <Panel header="Item Lifecycle Test" key="lifecycle">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Test Status: </Text>
                <Badge
                  status={item_lifecycle_test.success ? 'success' : 'error'}
                  text={item_lifecycle_test.success ? 'PASSED' : 'FAILED'}
                />
              </div>

              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Items Created"
                    value={item_lifecycle_test.items_created}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Transitions Tested"
                    value={item_lifecycle_test.transitions_tested}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Successful"
                    value={item_lifecycle_test.successful_transitions}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>

              {item_lifecycle_test.error && (
                <Alert
                  message="Test Error"
                  description={item_lifecycle_test.error}
                  type="error"
                />
              )}
            </Space>
          </Panel>
        </Collapse>
      </div>
    );
  };

  const renderRecommendations = () => {
    if (!testResults) return null;

    return (
      <div className={styles.recommendations}>
        <Title level={4}>Recommendations</Title>
        <List
          dataSource={testResults.recommendations}
          renderItem={(recommendation, index) => (
            <List.Item>
              <Space>
                <Badge count={index + 1} style={{ backgroundColor: '#1890ff' }} />
                <Text>{recommendation}</Text>
              </Space>
            </List.Item>
          )}
        />
      </div>
    );
  };

  const renderPropertyAnalysis = () => {
    if (!propertyAnalysis && !propertyValidation) {
      return (
        <div className={styles.propertyAnalysis}>
          <Alert
            message="No Property Data Available"
            description="Property analysis data is not available. Run a test to fetch property information."
            type="info"
            showIcon
          />
        </div>
      );
    }

    return (
      <div className="property-analysis">
        <Title level={4}>Property Analysis</Title>
        
        {propertyAnalysis && (
          <Card title="Property Summary" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Properties"
                  value={propertyAnalysis.property_summary?.total_properties || 0}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Material Properties"
                  value={propertyAnalysis.property_summary?.material_properties || 0}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Linked Properties"
                  value={propertyAnalysis.completeness_metrics?.material_properties_linked || 0}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Completeness Score"
                  value={Math.round((propertyAnalysis.completeness_metrics?.completeness_score || 0) * 100)}
                  suffix="%"
                />
              </Col>
            </Row>
          </Card>
        )}

        {propertyValidation && (
          <Card title="Property Validation" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                message={propertyValidation.overall_success ? "All Properties Valid" : "Property Issues Found"}
                type={propertyValidation.overall_success ? "success" : "warning"}
                showIcon
              />
              
              {propertyValidation.state_details && (
                <Collapse>
                  {propertyValidation.state_details.map((state: any, index: number) => (
                    <Panel 
                      header={`${state.state_name} (${state.total_properties} properties)`} 
                      key={index}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Row gutter={16}>
                          <Col span={8}>
                            <Text strong>Total Properties:</Text> {state.total_properties}
                          </Col>
                          <Col span={8}>
                            <Text strong>Material Properties:</Text> {state.material_properties}
                          </Col>
                          <Col span={8}>
                            <Text strong>Linked Properties:</Text> {state.linked_properties}
                          </Col>
                        </Row>
                        
                        {state.issues && state.issues.length > 0 && (
                          <div>
                            <Text strong>Issues:</Text>
                            <List
                              size="small"
                              dataSource={state.issues}
                              renderItem={(issue: string) => (
                                <List.Item>
                                  <Text type="danger">{issue}</Text>
                                </List.Item>
                              )}
                            />
                          </div>
                        )}
                      </Space>
                    </Panel>
                  ))}
                </Collapse>
              )}
            </Space>
          </Card>
        )}

        {propertyAnalysis?.recommendations && propertyAnalysis.recommendations.length > 0 && (
          <Card title="Property Recommendations">
            <List
              dataSource={propertyAnalysis.recommendations}
              renderItem={(recommendation: string) => (
                <List.Item>
                  <Space>
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    <Text>{recommendation}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className={styles.primeModelTester}>
      <Card
        title={
          <Space>
            <PlayCircleOutlined />
            <span>Prime Model Testing</span>
            {testResults && (
              <Badge
                status={testResults.overall_success ? 'success' : 'error'}
                text={testResults.overall_success ? 'PASSED' : 'FAILED'}
              />
            )}
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={runTest}
              loading={loading}
              disabled={loading}
            >
              {testResults ? 'Re-run Test' : 'Run Test'}
            </Button>
            {testResults && (
              <Button
                icon={<ReloadOutlined />}
                onClick={() => setTestResults(null)}
              >
                Clear Results
              </Button>
            )}
          </Space>
        }
      >
        {error && (
          <Alert
            message="Test Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {loading && (
          <div className={styles.testLoading}>
            <Spin size="large" />
            <Text>Running comprehensive test...</Text>
          </div>
        )}

        {testResults && (
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Overview" key="overview">
              {renderOverview()}
            </TabPane>
            <TabPane tab="Detailed Results" key="detailed">
              {renderDetailedResults()}
            </TabPane>
            <TabPane tab="Recommendations" key="recommendations">
              {renderRecommendations()}
            </TabPane>
            <TabPane tab="Property Analysis" key="properties">
              {renderPropertyAnalysis()}
            </TabPane>
          </Tabs>
        )}

        {!testResults && !loading && (
          <div className={styles.testPlaceholder}>
            <InfoCircleOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
            <Title level={4}>Ready to Test</Title>
            <Paragraph>
              Click "Run Test" to validate the prime model's connectivity and flow.
              This will create a temporary company instance and exercise the state machine.
            </Paragraph>
          </div>
        )}
      </Card>
    </div>
  );
};
